import { API } from "services/api";

export type StatusChamado = "ACAO_CLIENTE" | "EM_ANALISE" | "RESOLVIDO";

export type TipoChamado =
  | "ALFANDEGA"
  | "ENDERECO"
  | "ATRASO"
  | "TENTATIVA_ENTREGA"
  | "AVARIA"
  | "OUTRO";

export type EnvioDoChamado = {
  pedido_ref: string;
  carrier: string;
  tracking_number: string | null;
  comprador: string | null;
  destino: string;
};

export type Chamado = {
  id: number;
  cotacao_id: number | null;
  tipo: TipoChamado;
  titulo: string;
  /** "O que aconteceu", já em português. */
  descricao: string | null;
  /** "O que você precisa fazer". Null quando a bola está com a Intrex. */
  acao: string | null;
  status: StatusChamado;
  /** Data limite para o cliente responder (YYYY-MM-DD), ou null. */
  prazo: string | null;
  origem: "AUTO" | "CLIENTE";
  criado_em: string;
  resolvido_em: string | null;
  envio: EnvioDoChamado | null;
};

export type MensagemChamado = {
  id: number;
  autor: "CLIENTE" | "INTREX" | "TRANSPORTADORA";
  texto: string | null;
  anexo_nome: string | null;
  tem_anexo: boolean;
  criado_em: string;
};

export type ListaChamados = {
  ok: boolean;
  itens: Chamado[];
  abertas: number;
  resolvidas: number;
  acao_necessaria: number;
};

export const listarChamados = async (): Promise<ListaChamados> =>
  (await API.get<ListaChamados>("/api/chamados")).data;

export const verChamado = async (id: number) =>
  (
    await API.get<{ ok: boolean; chamado: Chamado; mensagens: MensagemChamado[] }>(
      `/api/chamados/${id}`,
    )
  ).data;

export const abrirChamado = async (payload: {
  titulo: string;
  texto?: string;
  cotacao_id?: number;
}) => (await API.post<{ ok: boolean; chamado: Chamado }>("/api/chamados", payload)).data;

/**
 * Responde no chamado, com anexo opcional.
 *
 * O arquivo vai em base64 no corpo, e não como multipart: é o mesmo caminho
 * que o resto do projeto já usa para documento, e o backend grava no Supabase
 * Storage. PDF, JPG ou PNG até 10 MB.
 */
export const responderChamado = async (
  id: number,
  payload: {
    texto?: string;
    anexo?: { base64: string; mime: string; nome: string };
  },
) =>
  (
    await API.post<{
      ok: boolean;
      mensagem: MensagemChamado;
      status: StatusChamado;
    }>(`/api/chamados/${id}/mensagens`, payload)
  ).data;

export const resolverChamado = async (id: number) =>
  (await API.post<{ ok: boolean }>(`/api/chamados/${id}/resolver`)).data;

export const baixarAnexo = async (chamadoId: number, mensagemId: number) =>
  (
    await API.get<Blob>(`/api/chamados/${chamadoId}/mensagens/${mensagemId}/anexo`, {
      responseType: "blob",
    })
  ).data;

/** Lê o arquivo do input como base64, no formato que a API espera. */
export const lerArquivo = (arquivo: File): Promise<{ base64: string; mime: string; nome: string }> =>
  new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onerror = () => reject(new Error("Não foi possível ler o arquivo."));
    leitor.onload = () =>
      resolve({
        base64: String(leitor.result ?? ""),
        mime: arquivo.type,
        nome: arquivo.name,
      });
    leitor.readAsDataURL(arquivo);
  });
